import React, { useState, useEffect } from 'react';
import { addDoc, onSnapshot, orderBy, query, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { musicBoxColl, musicBoxDoc } from '../db';
import { UserProfile, Screen } from '../App';

interface Props { user: UserProfile; navigate: (s: Screen) => void; }

interface Song {
  id: string;
  songName: string;
  artist: string;
  note: string;
  addedBy: string;
  addedByName: string;
  timestamp: any;
}

export default function MusicBox({ user, navigate }: Props) {
  const [songs, setSongs] = useState<Song[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [songName, setSongName] = useState('');
  const [artist, setArtist] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const q = query(musicBoxColl(user.coupleId), orderBy('timestamp', 'desc'));
    return onSnapshot(q, snap => setSongs(snap.docs.map(d => ({ id: d.id, ...d.data() } as Song))));
  }, [user.coupleId]);

  const add = async () => {
    if (!songName.trim()) return;
    setSaving(true);
    await addDoc(musicBoxColl(user.coupleId), {
      songName: songName.trim(),
      artist: artist.trim(),
      note: note.trim(),
      addedBy: user.uid,
      addedByName: user.displayName,
      timestamp: serverTimestamp(),
    });
    setSongName(''); setArtist(''); setNote('');
    setShowAdd(false); setSaving(false);
  };

  const remove = async (id: string, by: string) => {
    if (by !== user.uid) return;
    await deleteDoc(musicBoxDoc(user.coupleId, id));
  };

  const formatDate = (ts: any) => {
    if (!ts?.toDate) return '';
    return ts.toDate().toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  };

  return (
    <div>
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('memories')}>←</button>
        <h1 style={styles.title}>Müzik Kutusu</h1>
      </div>

      <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {songs.map(s => (
          <div key={s.id} className="card" style={styles.songCard}>
            <div style={styles.vinyl}>🎵</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={styles.songName}>{s.songName}</p>
              {s.artist && <p style={styles.artist}>{s.artist}</p>}
              {s.note && <p style={styles.songNote}>"{s.note}"</p>}
              <div style={styles.songMeta}>
                <span style={{ color: s.addedBy === user.uid ? '#FF6B9D' : '#C77DFF' }}>
                  {s.addedByName}
                </span>
                <span style={{ color: 'rgba(255,255,255,0.3)' }}>•</span>
                <span>{formatDate(s.timestamp)}</span>
              </div>
            </div>
            {s.addedBy === user.uid && (
              <button onClick={() => remove(s.id, s.addedBy)} style={styles.deleteBtn}>×</button>
            )}
          </div>
        ))}

        {songs.length === 0 && (
          <div style={styles.empty}>
            <span style={{ fontSize: 56 }}>🎶</span>
            <p>Müzik kutunuz boş</p>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>İlk şarkıyı ekle!</p>
          </div>
        )}
      </div>

      <button className="fab" onClick={() => setShowAdd(true)}>+</button>

      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <h2 style={{ fontFamily: "'Playfair Display',serif", marginBottom: 16, fontSize: 20 }}>
              🎵 Şarkı Ekle
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <input
                type="text"
                placeholder="Şarkı adı *"
                value={songName}
                onChange={e => setSongName(e.target.value)}
              />
              <input
                type="text"
                placeholder="Sanatçı (isteğe bağlı)"
                value={artist}
                onChange={e => setArtist(e.target.value)}
              />
              <textarea
                placeholder="Bu şarkı sana ne hissettiriyor?"
                value={note}
                onChange={e => setNote(e.target.value)}
                rows={2}
              />
              <button className="btn btn-primary" onClick={add} disabled={saving || !songName.trim()}>
                {saving ? '⏳ Kaydediliyor...' : '💾 Ekle'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  title: { fontFamily: "'Playfair Display',serif", fontSize: 22 },
  songCard: { padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: 14 },
  vinyl: { fontSize: 28, flexShrink: 0, marginTop: 2 },
  songName: { fontSize: 16, fontWeight: 600, marginBottom: 2, color: 'white' },
  artist: { fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 4 },
  songNote: {
    fontFamily: "'Playfair Display',serif", fontSize: 14, fontStyle: 'italic',
    color: 'rgba(255,255,255,0.75)', marginBottom: 8, lineHeight: 1.5,
  },
  songMeta: { display: 'flex', gap: 6, fontSize: 12, color: 'rgba(255,255,255,0.4)', alignItems: 'center' },
  deleteBtn: { background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', fontSize: 22, cursor: 'pointer', lineHeight: 1 },
  empty: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 8, padding: '60px 20px', color: 'rgba(255,255,255,0.5)', fontSize: 15, textAlign: 'center',
  },
};
